import { Container, Graphics, Rectangle, Sprite } from 'pixi.js';
import { getTexture } from '@/assets/resolver';
import { COLORS } from '@/config/colors';
import { t, type I18nKey } from '@/i18n';
import type { OfferCard } from '@/upgrades/pool';
import { cardDescription, cardName } from '@/upgrades/cards';
import { RARITY_COLOR, cardTint, drawCardIcon } from './card-icons';
import { GLYPH_INK } from './card-glyphs-combat';
import { drawHeadlines, fitText, levelPill } from './card-face';
import { cardHeadlines } from './card-headline';
import { cycleGlyph } from './hangar-icons';
import { CARD_C, MAIN_H, REROLL_TILE } from './shop-compact-layout';
import { buildLockButton } from './shop-cards';
import type { ShopState } from './shop-types';
import { chamferPanel, hexPath, label, targetBrackets } from './widgets';

/*
 * Phone CARDS page (polish 1): the shop card re-cut for a ~1:1 design
 * space — rarity band, ONE big glyph, the big signed number, the name; the
 * description only when a card has no number. Tray: padlock + gem price.
 */

const BAND = { x: 10, y: 6, h: 24 } as const;
const ICON = { y: 62, r: 26, scale: 0.78 } as const;
const HEAD = { one: 106, two: [100, 124], sizeOne: 26, sizeTwo: 19 } as const;
const NAME_Y = { none: 100, one: 128, two: 142 } as const;
const NAME_SIZE = 14;
const DESC = { top: 120, pad: 10, size: 13, min: 12 } as const;
const TRAY_GAP = 4;
/** Padlock hit box (≥ 44 CSS px on a phone) */
const LOCK_HIT = { w: 46, h: 44 } as const;
const PRICE_SIZE = 18;

function gemPrice(c: Container, cx: number, cy: number, price: number, red: boolean, on: boolean): void {
  const gem = new Sprite(getTexture(red ? 'pickup/gem-red' : 'pickup/gem-yellow'));
  gem.anchor.set(1, 0.5);
  gem.scale.set(0.55);
  gem.position.set(cx - 2, cy);
  const n = label(String(price), PRICE_SIZE, red ? COLORS.gemRed : COLORS.gemYellow, 1);
  n.anchor.set(0, 0.5);
  n.position.set(cx + 2, cy);
  gem.alpha = n.alpha = on ? 1 : 0.4;
  c.addChild(gem, n);
}

/** One offer card (CARD_C.w × CARD_C.h): face + tray. */
export function buildCompactCard(
  oc: OfferCard, wallet: { yellow: number; red: number }, selected: boolean, level: number,
  onToggleLock: () => void,
): Container {
  const { w, h, faceH } = CARD_C;
  const card = oc.card;
  const rarity = RARITY_COLOR[card.rarity];
  const tint = cardTint(card);
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, faceH, {
    fill: oc.bought ? 0x121019 : 0x161222,
    stroke: selected ? COLORS.uiAccent : rarity, strokeWidth: selected ? 3 : 2, notch: 12,
  });
  g.poly([BAND.x + 4, BAND.y, w - 8, BAND.y, w - 8, BAND.y + BAND.h, BAND.x, BAND.y + BAND.h])
    .fill({ color: rarity, alpha: 0.92 });
  hexPath(g, w / 2, ICON.y, ICON.r).fill(GLYPH_INK).stroke({ color: tint, width: 2.5 });
  c.addChild(g);

  const tag = label(t(`cardrarity.${card.rarity}` as I18nKey), 13, GLYPH_INK, 2);
  tag.anchor.set(0, 0.5);
  tag.position.set(BAND.x + 10, BAND.y + BAND.h / 2);
  c.addChild(tag);
  const pillW = levelPill(c, w - 12, BAND.y + BAND.h / 2, level);
  const room = w - 12 - pillW - BAND.x - 18;
  if (tag.width > room) tag.scale.set(room / tag.width);

  const icon = drawCardIcon(new Graphics(), card.icon, tint);
  icon.scale.set(ICON.scale);
  icon.position.set(w / 2, ICON.y);
  c.addChild(icon);

  const lines = cardHeadlines(card, level);
  drawHeadlines(c, lines, { cx: w / 2, ...HEAD });
  const name = label(cardName(card), NAME_SIZE);
  name.anchor.set(0.5, 0);
  name.position.set(w / 2, lines.length === 0 ? NAME_Y.none : lines.length > 1 ? NAME_Y.two : NAME_Y.one);
  if (name.width > w - 16) name.scale.set((w - 16) / name.width);
  c.addChild(name);
  if (lines.length === 0) {
    // No number to show: the effect text is the only way to tell
    const top = DESC.top;
    const body = fitText(cardDescription(card), 0xe2e6ee, w - DESC.pad * 2, faceH - top - 8, true, DESC.size, DESC.min);
    body.anchor.set(0.5, 0.5);
    body.position.set(w / 2, top + (faceH - top - 8) / 2);
    c.addChild(body);
  }

  // Tray: padlock | price (or ✓ once bought)
  const trayY = faceH + TRAY_GAP;
  const trayH = h - trayY;
  const tray = new Graphics();
  chamferPanel(tray, w, trayH, {
    fill: 0x0d0a16, stroke: selected ? COLORS.uiAccent : 0x2a2440, strokeWidth: 2, notch: 8, plain: true,
  });
  tray.position.set(0, trayY);
  c.addChild(tray);
  const cy = trayY + trayH / 2;
  if (oc.bought) {
    const tick = new Graphics();
    tick.moveTo(-12, 0).lineTo(-3, 9).lineTo(14, -10)
      .stroke({ color: COLORS.statUp, width: 5, cap: 'round', join: 'round' });
    tick.position.set(w / 2, cy);
    c.addChild(tick);
    c.alpha = 0.55;
  } else {
    const lock = buildLockButton(oc.locked, onToggleLock, LOCK_HIT.w / 2, cy);
    lock.hitArea = new Rectangle(-LOCK_HIT.w / 2, -LOCK_HIT.h / 2, LOCK_HIT.w, LOCK_HIT.h);
    c.addChild(lock);
    const afford = (oc.currency === 'red' ? wallet.red : wallet.yellow) >= oc.price;
    gemPrice(c, LOCK_HIT.w + (w - LOCK_HIT.w) / 2, cy, oc.price, oc.currency === 'red', afford);
  }
  if (selected) c.addChild(targetBrackets(new Graphics(), w, h, COLORS.uiAccent, 14, 4));
  return c;
}

/** Tall reroll tile beside the cards: ⟳ + yellow price. */
export function buildCompactReroll(s: ShopState, selected: boolean): Container {
  const w = REROLL_TILE.w;
  const h = MAIN_H;
  const on = s.yellow >= s.rerollPrice;
  const c = new Container();
  const g = new Graphics();
  chamferPanel(g, w, h, {
    fill: selected ? 0x2a2440 : 0x1a1626,
    stroke: selected ? COLORS.uiAccent : 0x4a4256, strokeWidth: selected ? 3 : 2, notch: 12,
  });
  cycleGlyph(g, w / 2, h * 0.38, 22, on ? COLORS.uiAccent : 0x5a6168);
  c.addChild(g);
  gemPrice(c, w / 2, h * 0.66, s.rerollPrice, false, on);
  if (selected) c.addChild(targetBrackets(new Graphics(), w, h, COLORS.uiAccent, 12, 4));
  return c;
}
